import { blue } from '@ant-design/colors'
import { faImage } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Modal, Upload, UploadFile } from 'antd'
import { ReactEventHandler, useEffect, useRef, useState } from 'react'
import { Crop, ReactCrop } from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'
import VSpace from '../common/VSpace'
import { cropImage, defaultCrop, isLoadedImage, isUsableCrop, MIN_CROP_SIZE } from './imageCrop'

export type Props = {
  open: boolean
  imageData?: string
  debug?: boolean
  onSubmit: (dataUrl: string) => void
  onCancel: () => void
}

const AvatarModal = ({ open, imageData, debug = false, onSubmit, onCancel }: Props) => {
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [crop, setCrop] = useState<Crop | undefined>()
  const [uncroppedDataUrl, setUncroppedDataUrl] = useState<string | undefined>(imageData)

  const uncroppedImageRef = useRef<HTMLImageElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    if (imageData) {
      setCrop(undefined)
      setUncroppedDataUrl(imageData)
    }
  }, [imageData])

  const onImageLoad: ReactEventHandler<HTMLImageElement> = (e) => {
    if (isLoadedImage(e.currentTarget)) {
      setCrop(defaultCrop(e.currentTarget))
    }
  }

  useEffect(() => {
    if (debug && canvasRef.current && uncroppedImageRef.current && isUsableCrop(crop)) {
      cropImage(canvasRef.current, uncroppedImageRef.current, crop)
    }
  }, [crop, debug])

  const handleOk = () => {
    if (uncroppedImageRef.current && isLoadedImage(uncroppedImageRef.current) && isUsableCrop(crop)) {
      const canvas = document.createElement('canvas')
      cropImage(canvas, uncroppedImageRef.current, crop)
      const dataUrl = canvas.toDataURL()
      onSubmit(dataUrl)
    }
  }

  const footerButtons = [
    <Button key="cancel" onClick={onCancel}>Cancel</Button>,
    <Button key="ok" type="primary" onClick={handleOk} disabled={!isUsableCrop(crop)}>OK</Button>
  ]

  return (
    <Modal open={open} title="Choose Your Avatar" footer={footerButtons} onCancel={onCancel} width={700}>
      {!uncroppedDataUrl && fileList.length === 0 && (
        <Upload.Dragger
          beforeUpload={() => false}
          fileList={fileList}
          onChange={({ fileList }) => setFileList(fileList)}
        >
          <FontAwesomeIcon icon={faImage} style={{ fontSize: 36, color: blue.primary }} />
          <p>Click or drag to add image</p>
        </Upload.Dragger>
      )}
      <VSpace size="middle" align="center" style={{ width: '100%' }}>
        {uncroppedDataUrl && (
          <ReactCrop crop={crop} onChange={setCrop} aspect={1} minWidth={MIN_CROP_SIZE} keepSelection circularCrop>
            <img src={uncroppedDataUrl} onLoad={onImageLoad} ref={uncroppedImageRef} style={{ maxWidth: 600 }} />
          </ReactCrop>
        )}
        {debug && isUsableCrop(crop) && (
          <canvas
            ref={canvasRef}
            style={{
              objectFit: 'contain',
              width: crop.width,
              height: crop.height,
            }}
          />
        )}
      </VSpace>
    </Modal>
  )
}

export default AvatarModal
